import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useLocation, useSearchParams } from "react-router-dom";
import { editClientLevel, getClientLevelById } from "../../../api/Services";
import {
  hasPermission,
  privilegesEnum,
} from "../../../helpers/PermissionHelper";
import {
  Form,
  Input,
  Button,
  message,
  Select,
  InputNumber,
  Switch,
  Skeleton,
} from "antd";
import { RxLayers } from "react-icons/rx";
import CustomBreadcrumb from "../../../components/breadcrumb/Breadcrumb";
import ActionButtons from "../../../components/actionbuttons/ActionButtons";

import styles from "./ViewClientLevel.module.scss";

const ViewClientLevel = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const getBasePath = () => {
    if (location.pathname.startsWith('/commercial-intelligence')) {
      return '/commercial-intelligence/configuration/client-levels';
    }
    if (location.pathname.startsWith('/fidelity')) {
      return '/fidelity/clientlevels';
    }
    return '/promotions/clientlevels';
  };

  const { id } = useParams();

  const [clientLevel, setClientLevel] = useState<any>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [editForm] = Form.useForm();

  const canEdit = hasPermission(privilegesEnum.WRITE_CLIENT_LEVEL);

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_CLIENT_LEVEL)) {
      handleFetchClientLevel();
    }
  }, []);

  const handleFetchClientLevel = async () => {
    const response = await getClientLevelById(id!);

    setClientLevel(response);
    setLoading(false);
  };

  const handleEdit = async () => {
    await editForm.validateFields().then(async (values) => {
      setSaving(true);
      const {
        name,
        level,
        description,
        ruleType,
        ruleQuantity,
        accumulationFactor,
        active,
      } = values;
      let response = await editClientLevel(parseInt(id!), {
        name,
        level,
        description,
        ruleType,
        ruleQuantity,
        accumulationFactor,
        active,
      });

      if (response) {
        message.success(t("Common-EditSuccessMessage"));
        await handleFetchClientLevel();
      } else message.error(t("Common-EditErrorMessage"));

      setSaving(false);
    });
  };
  const handleCancelButton = () => {
    editForm.resetFields();
  };

  return loading ? (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <Skeleton.Input style={{ width: 180, height: 20 }} active />
        </div>
        <div className={styles.actionButtonsWrapper}>
          <Skeleton.Button style={{ width: 80, height: 32 }} active />
          <Skeleton.Button style={{ width: 80, height: 32 }} active />
        </div>
      </div>

      <div className={styles.clientLevelInfoWrapper}>
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    </div>
  ) : !clientLevel ? (
    <div>Not Found</div>
  ) : (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <div>
          <CustomBreadcrumb
            items={[
              {
                icon: <RxLayers />,
                label: t("Common-ClientLevels"),
                path: `${getBasePath()}?${searchParams.toString()}`
              },
              {
                label: clientLevel?.name || t("Common-ClientLevel"),
                isCurrentPage: true
              }
            ]}
          />
        </div>
        <ActionButtons
          onSave={handleEdit}
          onCancel={handleCancelButton}
          loading={saving}
          disabled={saving}
          show={true}
          permission={privilegesEnum.WRITE_CLIENT_LEVEL}
        />
      </div>

      <div className={styles.clientLevelInfoWrapper}>
        <Form
          name="clientLevel"
          layout="vertical"
          initialValues={{ remember: true }}
          form={editForm}
          autoComplete="off"
          className={styles.newForm}
          preserve={false}
        >
          <div className={styles.itemWrapper}>
            <Form.Item
              initialValue={clientLevel?.level}
              label={t("Common-Level")}
              name="level"
              className={styles.formItem}
              rules={[
                {
                  required: true,
                  message: t("Common-Level"),
                },
              ]}
            >
              <InputNumber
                className={styles.inputNumber}
                placeholder={t("Common-Level")}
                disabled={!canEdit}
              />
            </Form.Item>
            <Form.Item
              initialValue={clientLevel?.name}
              label={t("Common-Name")}
              name="name"
              className={styles.formItem}
              rules={[
                {
                  required: true,
                  message: t("Common-Name"),
                },
              ]}
            >
              <Input placeholder={t("Common-Name")} disabled={!canEdit} />
            </Form.Item>
          </div>
          <div className={styles.itemWrapper}>
            <Form.Item
              initialValue={clientLevel?.description}
              label={t("Common-Description")}
              name="description"
              className={styles.formItem}
            >
              <Input placeholder={t("Common-Description")} disabled={!canEdit} />
            </Form.Item>
            <Form.Item
              initialValue={clientLevel?.accumulationFactor}
              label={t("Common-AccumulationFactor")}
              name="accumulationFactor"
              className={styles.formItem}
            >
              <InputNumber
                className={styles.inputNumber}
                placeholder={t("Common-AccumulationFactor")}
                disabled={!canEdit}
              />
            </Form.Item>
          </div>
          <div className={styles.itemWrapper}>
            <Form.Item
              label={t("Common-RuleType")}
              initialValue={clientLevel?.ruleType}
              name="ruleType"
              className={styles.formItem}
            >
              <Select
                allowClear
                style={{ width: "100%" }}
                placeholder={t("Common-RuleType")}
                disabled={!canEdit}
              >
                <Select.Option value="ACCOUNT_POINTS">
                  {t("Common-AccountPoints")}
                </Select.Option>
                <Select.Option value="MONTHS_IN_PROGRAM">
                  {t("Common-MonthsInProgram")}
                </Select.Option>
                <Select.Option value="TOTAL_SPENT">
                  {t("Common-TotalSpent")}
                </Select.Option>
                <Select.Option value="AMMOUNT_SPENT">
                  {t("Common-AmmountSpent")}
                </Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              initialValue={clientLevel?.ruleQuantity}
              label={t("Common-RuleQuantity")}
              name="ruleQuantity"
              className={styles.formItem}
            >
              <InputNumber
                className={styles.inputNumber}
                placeholder={t("Common-RuleQuantity")}
                disabled={!canEdit}
              />
            </Form.Item>
          </div>
          <div className={styles.itemWrapper}>
            <Form.Item
              initialValue={clientLevel?.active}
              label={t("Common-Active")}
              name="active"
              className={styles.formItem}
              rules={[
                {
                  required: true,
                  message: t("Common-Active"),
                },
              ]}
            >
              <Switch defaultChecked={clientLevel?.active} disabled={!canEdit} />
            </Form.Item>
          </div>
        </Form>
      </div>
    </div>
  );
};
export default ViewClientLevel;
